/**
 * Avatar Component
 *
 * Displays a user avatar image or a gradient initial fallback.
 * Supports multiple sizes for different contexts.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.src] - Image URL (renders img when provided)
 * @param {string} [props.srcSet] - srcSet attribute for responsive images
 * @param {string} [props.sizes] - sizes attribute for responsive images
 * @param {'eager'|'lazy'} [props.loading] - Image loading strategy (default: 'lazy')
 * @param {string} props.name - User name (initials derived for fallback)
 * @param {'sm'|'md'|'lg'|'xl'|'2xl'|'3xl'} [props.size] - Avatar size (default: 'md')
 * @param {string} [props.className] - Additional classes to merge
 * @param {string} [props.fallbackBg] - Background classes for fallback (default: blue gradient)
 */
import PropTypes from 'prop-types';

// Dimension classes shared by both image and fallback variants
const DIMENSION_CLASSES = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
  '2xl': 'w-24 h-24',
  '3xl': 'w-32 h-32',
};

// Text size classes used only by the initials fallback
const TEXT_CLASSES = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-4xl',
};

function getInitials(name) {
  if (!name) {return '?';}
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {return parts[0][0]?.toUpperCase() || '?';}
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Avatar({ src, srcSet, sizes, loading = 'lazy', name, size = 'md', className = '', fallbackBg = 'bg-gradient-to-br from-blue-500 to-blue-600' }) {
  const dimension = DIMENSION_CLASSES[size] || DIMENSION_CLASSES.md;
  const textSize = TEXT_CLASSES[size] || TEXT_CLASSES.md;

  if (src) {
    return (
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        loading={loading}
        alt={name}
        className={`${dimension} rounded-full object-cover ${className}`.trim()}
      />
    );
  }

  return (
    <div
      className={`${dimension} ${textSize} rounded-full ${fallbackBg} flex items-center justify-center text-white font-bold ${className}`.trim()}
    >
      {getInitials(name)}
    </div>
  );
}

Avatar.propTypes = {
  src: PropTypes.string,
  srcSet: PropTypes.string,
  sizes: PropTypes.string,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  name: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  className: PropTypes.string,
  fallbackBg: PropTypes.string,
};
